import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import {
  FaSearch, FaFilter, FaFileInvoice, FaMapMarkerAlt,
  FaBan, FaCheckCircle,
} from 'react-icons/fa'
import { FiFilter, FiRefreshCw, FiX, FiSearch as FiSearchIcon, FiUser, FiMapPin, FiSettings, FiFile } from 'react-icons/fi'
import { MdOutlineWorkOutline } from 'react-icons/md'
import { useAuth } from '../../provider/auth.provider'
import api from '../../libs/axios'
import { Pagination } from '../../utils/pagination.utils'
import { SelectWithSearch } from '../../utils/select-with-search'
import type { SelectOption } from '../../utils/select-with-search'
import toast from 'react-hot-toast'
import { useDebouncedCallback } from '../../hooks/useDebounce'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Site {
  id: number
  site_id: string
  site_name?: string
  reference_number?: string
  survey_workflow_status: string
  status?: string
  site_priority?: string
  site_engineer_work_date?: string | null
  report_designer_work_date?: string | null
  ta_work_date?: string | null
  validator_work_date?: string | null
  report_complete_date?: string | null
  report_sent_to_client_date?: string | null
  billing_status: string
  po_generated?: boolean
  vendor_po_not_required?: boolean
  po_number?: string | null
  po_date?: string | null
  billing_po_number?: string | null
  billing_po_date?: string | null
  invoice_number?: string | null
  invoice_date?: string | null
  item?: string | null
  item_description?: string | null
  working_month?: string | null
  sales_month?: string | null
  po_received_status?: string | null
  site_engineer_association_type?: string
  report_maker_association_type?: string
  vendor_owner_engineer?: string | null
  vendor_owner_report_maker?: string | null
  project?: { id: number; project_name: string; activity?: { id: number; activity_name: string } }
  site_engineer?: { id: number; first_name: string; last_name: string }
  report_designer?: { id: number; first_name: string; last_name: string }
  team_coordinator?: { id: number; first_name: string; last_name: string }
  mis?: { id: number; first_name: string; last_name: string }
  state_circle?: { id: number; state_name: string }
  district?: { id: number; name: string }
  vendor?: { id: number; vendor_full_name: string; vendor_short_name: string; vendor_code?: string; owner?: { id: number; first_name: string; last_name: string } | null }
  final_vendor?: { id: number; vendor_full_name: string; vendor_short_name: string; vendor_code?: string; owner?: { id: number; first_name: string; last_name: string } | null } | null
  stage_group_name?: string
}

interface PaginationData {
  page: number
  limit: number
  total_count: number
  total_pages: number
  has_next_page: boolean
  has_prev_page: boolean
}

interface Filters {
  vendor_id?: string
  vendor_label?: string
  site_engineer_id?: string
  site_engineer_label?: string
  report_maker_id?: string
  report_maker_label?: string
  survey_workflow_status?: string
  project_id?: string
  project_label?: string
  activity_id?: string
  activity_label?: string
  state_circle_id?: string
  state_circle_label?: string
  district_id?: string
  site_priority?: string
  date_from?: string
  date_to?: string
}

// Frozen (sticky) left columns for easier reading of the wide table (item 9c).
// Keys are TABLE_HEADERS indices: 0=Site ID, 1=Reference ID, 2=Site Name.
// `left` accounts for the 40px checkbox column that precedes them.
const FROZEN_COL: Record<number, { left: number; width: number }> = {
  0: { left: 40, width: 96 },
  1: { left: 136, width: 110 },
  2: { left: 246, width: 170 },
}

// ── Billing status config ─────────────────────────────────────────────────────
const BILLING_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  po_not_issued:    { label: 'PO Not Issued',      color: 'bg-gray-100 text-gray-500' },
  po_not_required:  { label: 'PO Not Required',     color: 'bg-orange-100 text-orange-700' },
  po_generated:     { label: 'PO Issued',           color: 'bg-green-100 text-green-700' },
  invoice_submitted:{ label: 'Invoice Submitted',   color: 'bg-blue-100 text-blue-700' },
  invoice_booked:   { label: 'Invoice Booked',      color: 'bg-emerald-100 text-emerald-700' },
  invoice_rejected: { label: 'Invoice Rejected',    color: 'bg-red-100 text-red-700' },
}

const BillingStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = BILLING_STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-500' }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      {cfg.label}
    </span>
  )
}

// ── PO Group Stage ─────────────────────────────────────────────────────────────
// Groups the billing/PO workflow status into three PO lifecycle stages (task 2g):
//   PO Not Issued            -> PO Pending
//   PO Not Required          -> PO Cancelled
//   PO Issued / Invoice*     -> PO Created
const PO_GROUP_STAGE_CONFIG: Record<string, { label: string; color: string }> = {
  po_not_issued:     { label: 'PO Pending',   color: 'bg-gray-100 text-gray-500' },
  po_not_required:   { label: 'PO Cancelled', color: 'bg-red-100 text-red-700' },
  po_generated:      { label: 'PO Created',   color: 'bg-green-100 text-green-700' },
  invoice_submitted: { label: 'PO Created',   color: 'bg-green-100 text-green-700' },
  invoice_rejected:  { label: 'PO Created',   color: 'bg-green-100 text-green-700' },
  invoice_booked:    { label: 'PO Created',   color: 'bg-green-100 text-green-700' },
}

const POGroupStageBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = PO_GROUP_STAGE_CONFIG[status] || { label: '—', color: 'bg-gray-100 text-gray-500' }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      {cfg.label}
    </span>
  )
}

// Work Completion Status — humanises project_sites.status (task 2f)
const formatWorkStatus = (s?: string | null) =>
  s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—'

const STATUS_MAP: Record<string, { color: string; label: string }> = {
  ta_review_completed: { color: 'bg-violet-100 text-violet-700', label: 'TA Review Completed' },
  mis_report_assign_pending: { color: 'bg-amber-100 text-amber-700', label: 'MIS Report Assign Pending' },
  report_maker_assigned: { color: 'bg-cyan-100 text-cyan-700', label: 'Report Maker Assigned' },
  report_in_progress: { color: 'bg-yellow-100 text-yellow-700', label: 'Report In Progress' },
  report_completed: { color: 'bg-blue-100 text-blue-700', label: 'Report Completed' },
  report_maker_rejected: { color: 'bg-red-100 text-red-700', label: 'Report Maker Rejected' },
  validator_assigned: { color: 'bg-indigo-100 text-indigo-700', label: 'Validator Assigned' },
  validation_in_progress: { color: 'bg-orange-100 text-orange-700', label: 'Validation In Progress' },
  validation_completed: { color: 'bg-teal-100 text-teal-700', label: 'Validation Completed' },
  validation_rejected: { color: 'bg-red-100 text-red-800', label: 'Validation Rejected' },
  report_finalized: { color: 'bg-lime-100 text-lime-700', label: 'Report Finalized' },
  report_sent_to_client: { color: 'bg-green-100 text-green-700', label: 'Report Sent To Client' },
}

const WORKFLOW_STATUS_OPTIONS = Object.entries(STATUS_MAP).map(([value, { label }]) => ({ id: value, value, label }))

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = STATUS_MAP[status] || { color: 'bg-gray-100 text-gray-700', label: status }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      {cfg.label}
    </span>
  )
}

// ── Filter Modal ──────────────────────────────────────────────────────────────
interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  filters: Filters
  onChange: (f: Filters) => void
  onApply: () => void
  onReset: () => void
}

const FilterModal: React.FC<FilterModalProps> = ({ isOpen, onClose, filters, onChange, onApply, onReset }) => {
  const [tab, setTab] = useState<'basic' | 'assignment' | 'location' | 'status'>('basic')

  const [vendorSel, setVendorSel] = useState<SelectOption | null>(
    filters.vendor_id ? { id: Number(filters.vendor_id), value: filters.vendor_id, label: filters.vendor_label || '' } : null
  )
  const [engineerSel, setEngineerSel] = useState<SelectOption | null>(
    filters.site_engineer_id ? { id: Number(filters.site_engineer_id), value: filters.site_engineer_id, label: filters.site_engineer_label || '' } : null
  )
  const [reportMakerSel, setReportMakerSel] = useState<SelectOption | null>(
    filters.report_maker_id ? { id: Number(filters.report_maker_id), value: filters.report_maker_id, label: filters.report_maker_label || '' } : null
  )
  const [projectSel, setProjectSel] = useState<SelectOption | null>(
    filters.project_id ? { id: Number(filters.project_id), value: filters.project_id, label: filters.project_label || '' } : null
  )
  const [activitySel, setActivitySel] = useState<SelectOption | null>(
    filters.activity_id ? { id: Number(filters.activity_id), value: filters.activity_id, label: filters.activity_label || '' } : null
  )
  const [stateSel, setStateSel] = useState<SelectOption | null>(
    filters.state_circle_id ? { id: Number(filters.state_circle_id), value: filters.state_circle_id, label: filters.state_circle_label || '' } : null
  )
  const [districtSel, setDistrictSel] = useState<SelectOption | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setTab('basic')
    setVendorSel(filters.vendor_id ? { id: Number(filters.vendor_id), value: filters.vendor_id, label: filters.vendor_label || '' } : null)
    setEngineerSel(filters.site_engineer_id ? { id: Number(filters.site_engineer_id), value: filters.site_engineer_id, label: filters.site_engineer_label || '' } : null)
    setReportMakerSel(filters.report_maker_id ? { id: Number(filters.report_maker_id), value: filters.report_maker_id, label: filters.report_maker_label || '' } : null)
    setProjectSel(filters.project_id ? { id: Number(filters.project_id), value: filters.project_id, label: filters.project_label || '' } : null)
    setActivitySel(filters.activity_id ? { id: Number(filters.activity_id), value: filters.activity_id, label: filters.activity_label || '' } : null)
    setStateSel(filters.state_circle_id ? { id: Number(filters.state_circle_id), value: filters.state_circle_id, label: filters.state_circle_label || '' } : null)
    setDistrictSel(null)
  }, [isOpen])

  if (!isOpen) return null

  const tabs = [
    { id: 'basic', label: 'Basic', icon: FiSearchIcon },
    { id: 'assignment', label: 'Assignment', icon: FiUser },
    { id: 'location', label: 'Location', icon: FiMapPin },
    { id: 'status', label: 'Status', icon: FiSettings },
  ] as const

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2"><FiFilter className="w-5 h-5" />Filters</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200"><FiX className="w-5 h-5" /></button>
        </div>

        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${tab === t.id ? 'border-blue-500 text-blue-600 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {tab === 'basic' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Vendor</label>
                <SelectWithSearch
                  placeholder="Search vendor..."
                  value={vendorSel}
                  onChange={(opt) => { setVendorSel(opt); onChange({ ...filters, vendor_id: opt ? String(opt.value) : undefined, vendor_label: opt?.label }) }}
                  apiEndpoint="/vendor/list"
                  dataKey="vendors"
                  labelKey="vendor_full_name"
                  valueKey="id"
                  searchParam="search"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Project</label>
                <SelectWithSearch
                  placeholder="Search project..."
                  value={projectSel}
                  onChange={(opt) => { setProjectSel(opt); onChange({ ...filters, project_id: opt ? String(opt.value) : undefined, project_label: opt?.label }) }}
                  apiEndpoint="/projects/list"
                  dataKey="projects"
                  labelKey="project_name"
                  valueKey="id"
                  searchParam="search"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Activity</label>
                <SelectWithSearch
                  placeholder="Search activity..."
                  value={activitySel}
                  onChange={(opt) => { setActivitySel(opt); onChange({ ...filters, activity_id: opt ? String(opt.value) : undefined, activity_label: opt?.label }) }}
                  apiEndpoint="/activities/list"
                  dataKey="activities"
                  labelKey="activity_name"
                  valueKey="id"
                  searchParam="search"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Field Work Date — From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                  value={filters.date_from || ''}
                  onChange={(e) => onChange({ ...filters, date_from: e.target.value || undefined })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Field Work Date — To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                  value={filters.date_to || ''}
                  onChange={(e) => onChange({ ...filters, date_to: e.target.value || undefined })}
                />
              </div>
            </div>
          )}

          {tab === 'assignment' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Site Engineer</label>
                <SelectWithSearch
                  placeholder="Search engineer..."
                  value={engineerSel}
                  onChange={(opt) => { setEngineerSel(opt); onChange({ ...filters, site_engineer_id: opt ? String(opt.value) : undefined, site_engineer_label: opt?.label }) }}
                  apiEndpoint="/users/list"
                  dataKey="users"
                  displayFormat={(u: any) => `${u.first_name} ${u.last_name}`}
                  valueKey="id"
                  searchParam="search"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Report Maker</label>
                <SelectWithSearch
                  placeholder="Search report maker..."
                  value={reportMakerSel}
                  onChange={(opt) => { setReportMakerSel(opt); onChange({ ...filters, report_maker_id: opt ? String(opt.value) : undefined, report_maker_label: opt?.label }) }}
                  apiEndpoint="/users/list"
                  dataKey="users"
                  displayFormat={(u: any) => `${u.first_name} ${u.last_name}`}
                  valueKey="id"
                  searchParam="search"
                  className="w-full"
                />
              </div>
            </div>
          )}

          {tab === 'location' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">State Circle</label>
                <SelectWithSearch
                  placeholder="Select State Circle"
                  value={stateSel}
                  onChange={(opt) => { setStateSel(opt); setDistrictSel(null); onChange({ ...filters, state_circle_id: opt ? String(opt.value) : undefined, state_circle_label: opt?.label, district_id: undefined }) }}
                  apiEndpoint="/state-circle/list"
                  dataKey="states"
                  labelKey="state_name"
                  valueKey="id"
                  searchParam="search"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">District</label>
                <SelectWithSearch
                  placeholder="Select District"
                  value={districtSel}
                  onChange={(opt) => { setDistrictSel(opt); onChange({ ...filters, district_id: opt ? String(opt.value) : undefined }) }}
                  apiEndpoint="/state-circle"
                  dataKey="districts"
                  labelKey="name"
                  valueKey="id"
                  searchParam="search"
                  dependencyId={stateSel ? Number(stateSel.value) : null}
                  className="w-full"
                />
              </div>
            </div>
          )}

          {tab === 'status' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Survey Workflow Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                  value={filters.survey_workflow_status || ''}
                  onChange={(e) => onChange({ ...filters, survey_workflow_status: e.target.value || undefined })}
                >
                  <option value="">All</option>
                  {WORKFLOW_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Site Priority</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                  value={filters.site_priority || ''}
                  onChange={(e) => onChange({ ...filters, site_priority: e.target.value || undefined })}
                >
                  <option value="">All</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button type="button" onClick={onReset} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-2">
            <FiRefreshCw className="w-4 h-4" />Reset
          </button>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={onApply} className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 flex items-center gap-2">
              <FiSearchIcon className="w-4 h-4" />Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Generate PO Offcanvas ─────────────────────────────────────────────────────
interface GeneratePOOffcanvasProps {
  isOpen: boolean
  onClose: () => void
  selectedCount: number
  onSubmit: (data: {
    item: string
    item_description: string
    working_month?: string
    sales_month?: string
    po_received_status?: string
  }) => void
  submitting: boolean
}

const GeneratePOOffcanvas: React.FC<GeneratePOOffcanvasProps> = ({ isOpen, onClose, selectedCount, onSubmit, submitting }) => {
  const [selectedItem, setSelectedItem] = useState<SelectOption | null>(null)
  const [description, setDescription] = useState('')
  const [workingMonth, setWorkingMonth] = useState('')
  const [salesMonth, setSalesMonth] = useState('')
  const [poReceivedStatus, setPoReceivedStatus] = useState('')

  useEffect(() => {
    if (isOpen) {
      setSelectedItem(null); setDescription('')
      setWorkingMonth(''); setSalesMonth(''); setPoReceivedStatus('')
    }
  }, [isOpen])

  const handleSubmit = () => {
    if (!selectedItem) return
    onSubmit({
      item: String(selectedItem.label),
      item_description: description.trim(),
      working_month: workingMonth || undefined,
      sales_month: salesMonth || undefined,
      po_received_status: poReceivedStatus || undefined,
    })
  }

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black bg-opacity-40 z-40" onClick={onClose} />}
      <div className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <FaFileInvoice className="w-5 h-5" />Generate PO
            </h2>
            <p className="text-blue-200 text-xs mt-0.5">{selectedCount} site{selectedCount !== 1 ? 's' : ''} selected — PO numbers are auto-assigned</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200"><FiX className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Invoice Item Name <span className="text-red-500">*</span>
            </label>
            <SelectWithSearch
              placeholder="Search item..."
              value={selectedItem}
              onChange={(opt) => setSelectedItem(opt)}
              apiEndpoint="/vendor-invoice-items/list"
              dataKey="items"
              labelKey="item_name"
              valueKey="id"
              searchParam="search"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Invoice Description</label>
            <textarea
              className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Describe the work being billed..."
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="flex items-start gap-2 rounded border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs text-blue-800">
            <FiFile className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              The PO Document is generated automatically once the PO is issued and attached to
              the vendor invoice — no upload needed.
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">ESPL PO Received Status</label>
            <select
              className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white"
              value={poReceivedStatus}
              onChange={(e) => setPoReceivedStatus(e.target.value)}
            >
              <option value="">-- Select --</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Working Month</label>
            <input
              type="month"
              className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              value={workingMonth}
              onChange={(e) => setWorkingMonth(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Sales Month</label>
            <input
              type="month"
              className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              value={salesMonth}
              onChange={(e) => setSalesMonth(e.target.value)}
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button type="button" onClick={onClose} disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedItem || submitting}
            onClick={handleSubmit}
            className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            {submitting
              ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Generating...</>
              : <><FaFileInvoice className="w-4 h-4" />Generate PO</>}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────────
type WorkAcceptanceMode = 'field_work' | 'report_prep' | 'complete'

const MODE_LABELS: Record<WorkAcceptanceMode, string> = {
  field_work: 'Field Work Sites',
  report_prep: 'Report Prep Sites',
  complete: 'Complete Sites',
}

const VendorWorkAcceptance: React.FC<{ mode?: WorkAcceptanceMode }> = ({ mode }) => {
  const { hasPermission } = useAuth()
  const canGeneratePO = hasPermission('VendorInvoicing', 'generate_vendor_po')

  const [sites, setSites] = useState<Site[]>([])
  const [pagination, setPagination] = useState<PaginationData>({ page: 1, limit: 20, total_count: 0, total_pages: 1, has_next_page: false, has_prev_page: false })
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [advFilters, setAdvFilters] = useState<Filters>({})
  const [pendingFilters, setPendingFilters] = useState<Filters>({})
  const [filterOpen, setFilterOpen] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [billingModalOpen, setBillingModalOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [togglingId, setTogglingId] = useState<number | null>(null)

  const activeFilterCount = useMemo(() =>
    Object.entries(advFilters).filter(([k, v]) => v && !k.endsWith('_label')).length,
    [advFilters]
  )

  const fetchSites = useCallback(async (params: { search?: string; page?: number; limit?: number } & Filters = {}) => {
    setLoading(true)
    try {
      const q: any = { page: params.page || pagination.page, limit: params.limit || pagination.limit }
      if (params.search !== undefined) q.search = params.search || undefined
      else if (search) q.search = search

      const merged = { ...advFilters, ...params }
      const filterKeys: (keyof Filters)[] = ['vendor_id', 'survey_workflow_status', 'site_engineer_id', 'report_maker_id', 'project_id', 'activity_id', 'state_circle_id', 'district_id', 'site_priority', 'date_from', 'date_to']
      filterKeys.forEach((k) => { if (merged[k]) q[k] = merged[k] })

      if (mode) q.assignment_mode = mode

      const res = await api.get('/billing/vendor-work-acceptance', { params: q })
      if (res.data.success) {
        setSites(res.data.data.rows || [])
        setPagination(res.data.data.pagination || pagination)
        setSelected(new Set())
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [advFilters, search, pagination.page, pagination.limit, mode])

  useEffect(() => { fetchSites({ page: 1 }) }, [mode])

  const debouncedSearch = useDebouncedCallback((val: string) => {
    fetchSites({ search: val, page: 1 })
  }, 400)

  // Only sites with billing_status = 'po_not_issued' can be selected for PO generation
  const canSelectSiteForPO = (site: Site) => site.billing_status === 'po_not_issued'

  // Checkbox logic: can only select same-vendor sites that are eligible
  const selectedVendorId = useMemo(() => {
    if (selected.size === 0) return null
    const firstId = Array.from(selected)[0]
    const site = sites.find((s) => s.id === firstId)
    return site?.vendor?.id ?? null
  }, [selected, sites])

  const canSelectSite = (site: Site) => {
    if (!canSelectSiteForPO(site)) return false
    if (selected.size === 0) return true
    return site.vendor?.id === selectedVendorId
  }

  const toggleSite = (site: Site) => {
    if (!canSelectSiteForPO(site)) return
    if (!canSelectSite(site) && !selected.has(site.id)) {
      toast.error('You can only select sites from the same vendor at a time')
      return
    }
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(site.id)) next.delete(site.id)
      else next.add(site.id)
      return next
    })
  }

  const sameVendorEligibleSites = useMemo(() => {
    if (!selectedVendorId) return sites.filter(canSelectSiteForPO)
    return sites.filter((s) => s.vendor?.id === selectedVendorId && canSelectSiteForPO(s))
  }, [sites, selectedVendorId])

  const allSameVendorSelected = sameVendorEligibleSites.length > 0 && sameVendorEligibleSites.every((s) => selected.has(s.id))

  const handleSelectAll = () => {
    if (allSameVendorSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(sameVendorEligibleSites.map((s) => s.id)))
    }
  }

  const handleGeneratePO = async (data: {
    item: string
    item_description: string
    working_month?: string
    sales_month?: string
    po_received_status?: string
  }) => {
    setSubmitting(true)
    try {
      await api.post('/billing/vendor-site-invoices/generate-po', {
        site_ids: Array.from(selected),
        assignment_mode: mode,
        ...data,
      })
      toast.success('PO generated successfully')
      setBillingModalOpen(false)
      setSelected(new Set())
      fetchSites({ page: 1 })
    } catch {
      // toast handled by axios interceptor
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleNotRequired = async (site: Site) => {
    setTogglingId(site.id)
    try {
      await api.patch(`/billing/vendor-work-acceptance/${site.id}/toggle-not-required`)
      fetchSites({ page: pagination.page })
    } catch {
      // toast handled by axios interceptor
    } finally {
      setTogglingId(null)
    }
  }

  const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

  const TABLE_HEADERS = [
    'Site ID', 'Reference ID', 'Site Name', 'Activity', 'Project Name',
    'Site Engineer', 'Report Maker', 'Final Vendor Name',
    'FW Completion Date', 'Report Complete Date', 'Report Validation Date',
    'Report Sent to Client', 'Site Stage', 'Stage Group Name', 'Work Completion Status',
    'Team Coordinator', 'MIS Name',
    'Vendor Invoice Stages', 'PO Group Stage', 'Vendor PO Number', 'Vendor PO Date',
    'Vendor Invoice Number', 'Vendor Invoice Date',
    'ESPL Billed PO Number', 'ESPL Billed PO Date',
    'ESPL PO Received Status', 'Item', 'Item Description',
    'State', 'District', 'Action',
  ]

  return (
    <>
      <Helmet><title>Generate PO – Vendor Work Acceptance</title></Helmet>
      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <MdOutlineWorkOutline className="w-6 h-6 text-blue-700" />
                {mode ? MODE_LABELS[mode] : 'Vendor Work Acceptance'}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">Select sites of the same vendor to generate a PO</p>
            </div>
            {selected.size > 0 && canGeneratePO && (
              <button
                type="button"
                onClick={() => setBillingModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors shadow-sm"
              >
                <FaFileInvoice className="w-4 h-4" />
                Generate PO ({selected.size})
              </button>
            )}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50/30 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 max-w-md relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by site ID or name..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); debouncedSearch(e.target.value) }}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <button
            type="button"
            onClick={() => { setPendingFilters(advFilters); setFilterOpen(true) }}
            className={`inline-flex items-center gap-2 px-4 py-2 border rounded text-sm font-medium transition-colors ${activeFilterCount > 0 ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            <FiFilter className="w-4 h-4" />Filters
            {activeFilterCount > 0 && (
              <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{activeFilterCount}</span>
            )}
          </button>
        </div>

        {/* Selection info banner */}
        {selected.size > 0 && (
          <div className="px-4 sm:px-6 py-2 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
            <span className="text-sm text-blue-700 font-medium">
              {selected.size} site{selected.size !== 1 ? 's' : ''} selected
              {selectedVendorId && <> — Vendor: <strong>{sites.find((s) => s.vendor?.id === selectedVendorId)?.vendor?.vendor_full_name}</strong></>}
            </span>
            <button type="button" onClick={() => setSelected(new Set())} className="text-xs text-blue-600 hover:text-blue-800 underline">Clear selection</button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 bg-gray-50" style={{ position: 'sticky', left: 0, width: 40, minWidth: 40, zIndex: 20 }}>
                  <input
                    type="checkbox"
                    checked={allSameVendorSelected && sameVendorEligibleSites.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    disabled={!canGeneratePO}
                    title={selectedVendorId ? `Select all eligible from same vendor` : 'Select all eligible'}
                  />
                </th>
                {TABLE_HEADERS.map((h, i) => {
                  const frozen = FROZEN_COL[i]
                  return (
                    <th
                      key={h}
                      className={`px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap ${frozen ? 'bg-gray-50' : ''}`}
                      style={frozen ? { position: 'sticky', left: frozen.left, width: frozen.width, minWidth: frozen.width, zIndex: 20 } : undefined}
                    >
                      {h}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={TABLE_HEADERS.length + 1} className="py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                      <span className="text-sm text-gray-500">Loading sites...</span>
                    </div>
                  </td>
                </tr>
              ) : sites.length === 0 ? (
                <tr>
                  <td colSpan={TABLE_HEADERS.length + 1} className="py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center">
                        <FaMapMarkerAlt className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">No eligible vendor sites found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                sites.map((site) => {
                  const isChecked = selected.has(site.id)
                  const eligible = canSelectSiteForPO(site)
                  const canCheck = canGeneratePO && canSelectSite(site)
                  const frozenBg = isChecked ? 'bg-blue-50' : 'bg-white'
                  return (
                    <tr key={site.id} className={`hover:bg-gray-50 transition-colors ${isChecked ? 'bg-blue-50' : ''} ${!eligible ? 'opacity-75' : ''}`}>
                      <td className={`px-3 py-3 ${frozenBg}`} style={{ position: 'sticky', left: 0, width: 40, minWidth: 40, zIndex: 10 }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleSite(site)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          disabled={!eligible || (!canCheck && !isChecked)}
                          title={
                            !eligible
                              ? `Status: ${BILLING_STATUS_CONFIG[site.billing_status]?.label || site.billing_status}`
                              : !canCheck && !isChecked
                                ? 'Can only select sites of the same vendor'
                                : ''
                          }
                        />
                      </td>
                      <td className={`px-3 py-3 text-xs font-mono text-gray-700 whitespace-nowrap ${frozenBg}`} style={{ position: 'sticky', left: FROZEN_COL[0].left, width: FROZEN_COL[0].width, minWidth: FROZEN_COL[0].width, zIndex: 10 }}>{site.site_id}</td>
                      <td className={`px-3 py-3 text-xs text-gray-500 font-mono ${frozenBg}`} style={{ position: 'sticky', left: FROZEN_COL[1].left, width: FROZEN_COL[1].width, minWidth: FROZEN_COL[1].width, zIndex: 10 }}>{site.reference_number || '—'}</td>
                      <td className={`px-3 py-3 text-xs text-gray-900 ${frozenBg}`} style={{ position: 'sticky', left: FROZEN_COL[2].left, width: FROZEN_COL[2].width, minWidth: FROZEN_COL[2].width, zIndex: 10 }}>{site.site_name || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{site.project?.activity?.activity_name || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-900 whitespace-nowrap">{site.project?.project_name || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">
                        {site.site_engineer ? `${site.site_engineer.first_name} ${site.site_engineer.last_name}` : '—'}
                        {site.site_engineer_association_type && <div className="text-gray-400 text-xs">{site.site_engineer_association_type}</div>}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">
                        {site.report_designer ? `${site.report_designer.first_name} ${site.report_designer.last_name}` : '—'}
                        {site.report_maker_association_type && <div className="text-gray-400 text-xs">{site.report_maker_association_type}</div>}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">
                        {site.vendor ? (
                          <div>
                            <div>{site.vendor.vendor_full_name}</div>
                            {site.vendor.vendor_code && <div className="text-gray-400">{site.vendor.vendor_code}</div>}
                            {site.vendor.owner && (
                              <div className="text-gray-400">Owner: {site.vendor.owner.first_name} {site.vendor.owner.last_name}</div>
                            )}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fmtDate(site.site_engineer_work_date)}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fmtDate(site.report_complete_date)}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fmtDate(site.validator_work_date)}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fmtDate(site.report_sent_to_client_date)}</td>
                      <td className="px-3 py-3"><StatusBadge status={site.survey_workflow_status} /></td>
                      <td className="px-3 py-3 text-xs whitespace-nowrap">
                        {site.stage_group_name ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium">
                            {site.stage_group_name}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{formatWorkStatus(site.status)}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">
                        {site.team_coordinator ? `${site.team_coordinator.first_name} ${site.team_coordinator.last_name}` : '—'}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">
                        {site.mis ? `${site.mis.first_name} ${site.mis.last_name}` : '—'}
                      </td>
                      <td className="px-3 py-3"><BillingStatusBadge status={site.billing_status} /></td>
                      <td className="px-3 py-3"><POGroupStageBadge status={site.billing_status} /></td>
                      <td className="px-3 py-3 text-xs text-gray-500 font-mono">{site.billing_po_number || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fmtDate(site.billing_po_date)}</td>
                      <td className="px-3 py-3 text-xs text-gray-500 font-mono">{site.invoice_number || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fmtDate(site.invoice_date)}</td>
                      <td className="px-3 py-3 text-xs text-gray-500 font-mono">{site.po_number || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fmtDate(site.po_date)}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 capitalize">{site.po_received_status || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{site.item || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 max-w-xs truncate">{site.item_description || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{site.state_circle?.state_name || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{site.district?.name || '—'}</td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {canGeneratePO && (site.billing_status === 'po_not_issued' || site.billing_status === 'po_not_required') && (
                          <button
                            type="button"
                            onClick={() => handleToggleNotRequired(site)}
                            disabled={togglingId === site.id}
                            title={site.billing_status === 'po_not_required' ? 'Mark as Available' : 'Mark as Not Required'}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border transition-colors disabled:opacity-50 ${
                              site.billing_status === 'po_not_required'
                                ? 'border-green-300 text-green-700 hover:bg-green-50'
                                : 'border-orange-300 text-orange-700 hover:bg-orange-50'
                            }`}
                          >
                            {togglingId === site.id
                              ? <div className="animate-spin rounded-full h-3 w-3 border-b border-current" />
                              : site.billing_status === 'po_not_required'
                                ? <><FaCheckCircle className="w-3 h-3" />Mark Available</>
                                : <><FaBan className="w-3 h-3" />Not Required</>}
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          pagination={pagination as any}
          onPageChange={(p) => fetchSites({ page: p })}
          onLimitChange={(l) => { setPagination((prev) => ({ ...prev, limit: l })); fetchSites({ page: 1, limit: l }) }}
        />
      </div>

      <FilterModal
        isOpen={filterOpen}
        onClose={() => { setFilterOpen(false); setPendingFilters(advFilters) }}
        filters={pendingFilters}
        onChange={setPendingFilters}
        onApply={() => { setAdvFilters(pendingFilters); setFilterOpen(false); fetchSites({ page: 1, ...pendingFilters }) }}
        onReset={() => { setAdvFilters({}); setPendingFilters({}); setFilterOpen(false); fetchSites({ page: 1 }) }}
      />

      <GeneratePOOffcanvas
        isOpen={billingModalOpen}
        onClose={() => setBillingModalOpen(false)}
        selectedCount={selected.size}
        onSubmit={handleGeneratePO}
        submitting={submitting}
      />
    </>
  )
}

export default VendorWorkAcceptance
